import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { Geometry } from 'geojson';
import config from 'constants/apiConfig';
import { parseError } from 'hooks/shared/error';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid } from 'slices/commonSelectors';
import type {
  OrgEventChecklistConfig,
  OrgEventNotifyConfig,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import type { EscalationPlan } from 'components/MapPage/CorporateViewTabs/NotificationsTab/escalationPlanModel';

/**
 * A scenario is an automation rule with the trigger removed, so the config
 * shapes are the automation rules' own. Sharing the types is what lets the rule
 * editor's checklist and notify steps serve both.
 */
export type ScenarioAudienceConfig = {
  userIds?: string[];
  teamIds?: string[];
  aorItemIds?: string[];
  geometry?: Geometry | null;
};

export type Scenario = {
  id: string;
  groupGuid: string;
  name: string;
  description: string | null;
  audienceConfig: ScenarioAudienceConfig;
  notifyConfig: OrgEventNotifyConfig;
  escalationPlan: EscalationPlan | null;
  checklistConfig: OrgEventChecklistConfig;
  archived: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ScenarioWritePayload = {
  name: string;
  description?: string | null;
  audienceConfig?: ScenarioAudienceConfig;
  notifyConfig?: OrgEventNotifyConfig;
  escalationPlan?: EscalationPlan | null;
  checklistConfig?: OrgEventChecklistConfig;
};

export type ScenarioLaunch = {
  id: string;
  scenarioId: string;
  incidentId: string | null;
  orgEventId: string | null;
  notificationId: string | null;
  createdActionIds: string[];
  recipientCount: number;
  launchedBy: string | null;
  launchedAt: string;
};

export type ScenarioPreview = {
  scenario: Scenario;
  recipientCount: number;
  aorNames: string[];
  actionLabels: string[];
  emptyAudience: boolean;
};

export type ScenarioLaunchResult = {
  launchId: string;
  notificationId: string | null;
  createdActionIds: string[];
  recipientCount: number;
  emptyAudience: boolean;
};

export const scenarioKeys = {
  all: ['scenarios'] as const,
  list: (groupGuid: string) =>
    [...scenarioKeys.all, 'list', groupGuid] as const,
  launches: (scenarioId: string) =>
    [...scenarioKeys.all, 'launches', scenarioId] as const,
};

export function useScenarios(options?: { enabled?: boolean }) {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery({
    queryKey: scenarioKeys.list(groupGuid ?? ''),
    enabled: Boolean(groupGuid) && options?.enabled !== false,
    queryFn: async () => {
      const response = await axios.get<Scenario[]>(
        config.apiGateway.scenarios,
        {
          params: { groupGuid },
        }
      );
      return response.data ?? [];
    },
  });
}

export function useCreateScenario() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ScenarioWritePayload) => {
      const response = await axios.post<Scenario>(config.apiGateway.scenarios, {
        ...payload,
        groupGuid,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: scenarioKeys.list(groupGuid ?? ''),
      });
    },
    onError: (error) => toast.error(parseError(error)),
  });
}

export function useUpdateScenario() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: ScenarioWritePayload & { id: string }) => {
      const response = await axios.put<Scenario>(
        `${config.apiGateway.scenarios}/${id}`,
        { ...payload, groupGuid }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: scenarioKeys.list(groupGuid ?? ''),
      });
    },
    onError: (error) => toast.error(parseError(error)),
  });
}

export function useArchiveScenario() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${config.apiGateway.scenarios}/${id}`, {
        params: { groupGuid },
      });
      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: scenarioKeys.list(groupGuid ?? ''),
      });
    },
    onError: (error) => toast.error(parseError(error)),
  });
}

export function useScenarioLaunches(scenarioId: string | null) {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery({
    queryKey: scenarioKeys.launches(scenarioId ?? ''),
    enabled: Boolean(scenarioId) && Boolean(groupGuid),
    queryFn: async () => {
      const response = await axios.get<ScenarioLaunch[]>(
        `${config.apiGateway.scenarios}/${scenarioId}/launches`,
        { params: { groupGuid } }
      );
      return response.data ?? [];
    },
  });
}

/**
 * What launching would do. A mutation rather than a query because it is asked
 * for at the moment the operator opens the confirm dialog, and the answer
 * changes with the overrides they are choosing there.
 */
export function usePreviewScenario() {
  const groupGuid = useSelector(selectGroupGuid);
  return useMutation({
    mutationFn: async (input: {
      id: string;
      overrides?: {
        audienceConfig?: ScenarioAudienceConfig;
        skipChecklist?: boolean;
      };
    }) => {
      const response = await axios.post<ScenarioPreview>(
        `${config.apiGateway.scenarios}/${input.id}/preview`,
        { groupGuid, overrides: input.overrides }
      );
      return response.data;
    },
  });
}

export function useLaunchScenario() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      incidentId?: string | null;
      orgEventId?: string | null;
      overrides?: {
        audienceConfig?: ScenarioAudienceConfig;
        skipChecklist?: boolean;
        skipNotify?: boolean;
      };
    }) => {
      const response = await axios.post<ScenarioLaunchResult>(
        `${config.apiGateway.scenarios}/${input.id}/launch`,
        {
          groupGuid,
          incidentId: input.incidentId,
          orgEventId: input.orgEventId,
          overrides: input.overrides,
        }
      );
      return response.data;
    },
    onSuccess: (result, variables) => {
      if (result.emptyAudience) {
        toast.warn(
          'Nobody matched this scenario’s audience, so no notification was sent.'
        );
      } else {
        toast.success(
          `Scenario launched to ${result.recipientCount} recipients.`
        );
      }

      void queryClient.invalidateQueries({
        queryKey: scenarioKeys.launches(variables.id),
      });
      void queryClient.invalidateQueries({ queryKey: ['orgActions'] });
      void queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      void queryClient.invalidateQueries({
        queryKey: ['notificationSends', groupGuid],
      });
    },
    onError: (error) => toast.error(parseError(error)),
  });
}
